import api from "@/lib/api";

// Iteration 250 — Web Push (VAPID) + Home Screen badging helpers.

export function isPushSupported() {
  return (
    typeof window !== "undefined" &&
    window.isSecureContext &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export function urlBase64ToUint8Array(base64) {
  const pad = "=".repeat((4 - (base64.length % 4)) % 4);
  const raw = atob((base64 + pad).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

export async function registerPushWorker() {
  if (!("serviceWorker" in navigator)) return null;
  try {
    return await navigator.serviceWorker.register("/push-sw.js", { scope: "/" });
  } catch (_) {
    return null;
  }
}

export async function enablePush() {
  if (!isPushSupported()) {
    throw new Error("Push notifications need a secure (HTTPS) browser with service worker support.");
  }
  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    throw new Error("Notification permission was not granted — check your browser/site settings.");
  }
  const registration =
    (await navigator.serviceWorker.getRegistration("/")) || (await registerPushWorker());
  if (!registration) throw new Error("Service worker unavailable.");
  await navigator.serviceWorker.ready;
  const { data } = await api.get("/push/vapid-public-key");
  let subscription = await registration.pushManager.getSubscription();
  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(data.public_key),
    });
  }
  await api.post("/push/subscribe", subscription.toJSON());
  return subscription;
}

export async function disablePush() {
  try {
    const registration = await navigator.serviceWorker.getRegistration("/");
    const subscription = registration && (await registration.pushManager.getSubscription());
    if (subscription) {
      try { await api.post("/push/unsubscribe", subscription.toJSON()); } catch (_) { /* best-effort */ }
      await subscription.unsubscribe();
    }
  } catch (_) { /* best-effort */ }
}

export function setAppUnreadBadge(count) {
  try {
    if ("setAppBadge" in navigator) {
      if (count > 0) navigator.setAppBadge(count).catch(() => {});
      else if ("clearAppBadge" in navigator) navigator.clearAppBadge().catch(() => {});
    }
  } catch (_) { /* unsupported platform */ }
}

export function clearAppUnreadBadge() {
  try {
    if ("clearAppBadge" in navigator) navigator.clearAppBadge().catch(() => {});
  } catch (_) { /* unsupported platform */ }
}
